import { InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useCallback, useMemo } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import {
  deleteInventory,
  inventoryByIdSelector,
  setMemoizedItem,
  tradeSelector,
} from "@/domains/inventory/slice";
import getDisabledItemsByFilters from "@/domains/inventory/helpers/getDisabledItemsByFilters";
import useTradeManager from "@/domains/inventory/hooks/trade/useTradeManager";
import useContextMenu from "@/domains/inventory/hooks/useContextMenu";
import useFilterItems from "@/domains/inventory/hooks/useFilterItems";
import useSearchItems from "@/domains/inventory/hooks/useSearchItems";
import InventoryCommonHeader from "@/domains/inventory/components/InventoryCommonHeader";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryFilter from "@/domains/inventory/components/InventoryFilter";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventorySearch from "@/domains/inventory/components/InventorySearch";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryContextMenu from "@/domains/inventory/components/contextMenu/InventoryContextMenu";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";

type Props = { inventoryId: number };

export default function InventoryTradeBagContainer({ inventoryId }: Props) {
  const { handleContextMenu, isOpen, menuRef, position, itemForContextMenu, closeContextMenu } =
    useContextMenu();
  const dispatch = useAppDispatch();
  const inventory = useAppSelector((state) => inventoryByIdSelector(state, inventoryId));
  const { filteredItems, filterGroups, selectedGroups, onSelectHandler } = useFilterItems(
    inventory?.items || [],
  );
  const { onChangeSearchValue, deferredSearchValue, itemsBySearch } = useSearchItems(filteredItems);

  const { removeItemFromTrade, moveInventoryItem, addItemToTrade } = useTradeManager();
  const trade = useAppSelector(tradeSelector);

  const momoizedItemHandler = useCallback(
    (item: InventoryItem | null) => dispatch(setMemoizedItem(item)),
    [],
  );

  const onClose = useCallback(() => {
    dispatch(deleteInventory(inventoryId));
  }, [inventoryId]);

  const disabledItems = useMemo(() => {
    if (inventory) {
      return getDisabledItemsByFilters(inventory.items, itemsBySearch);
    }
    return [];
  }, [inventory, itemsBySearch]);

  if (!inventory) return null;

  return (
    <InventoryWrapper className="max-h-full">
      <InventoryHeader className="px-0 border border-b-0 border-dark-900 rounded-t-[inherit] overflow-clip">
        <InventoryCommonHeader
          onClose={onClose}
          ownerType={OwnerType.Bag}
          iconClassName="text-accent-solid"
        />
      </InventoryHeader>
      <InventoryContent className="flex items-center justify-between w-full h-[3rem]">
        <InventorySearch
          value={deferredSearchValue}
          onChange={onChangeSearchValue}
          className="w-fit"
        />
        {filterGroups.length > 0 && (
          <InventoryFilter
            groups={filterGroups}
            selectedGroups={selectedGroups}
            onSelect={onSelectHandler}
          />
        )}
      </InventoryContent>
      <InventoryContent className="flex-1 overflow-clip overflow-y-auto py-6">
        <InventoryGrid
          inventoryId={inventory.id}
          cols={inventory.size.width}
          rows={inventory.size.height}
          disabledItems={disabledItems}
          items={inventory.items}
          onMove={moveInventoryItem}
          onRemoveItemFromTrade={removeItemFromTrade}
          onMemoizedItem={momoizedItemHandler}
          onContextMenu={handleContextMenu}
        />
        {itemForContextMenu && isOpen && (
          <InventoryContextMenu
            ref={menuRef}
            position={position}
            item={itemForContextMenu}
            onTrade={() => {
              addItemToTrade(inventoryId, 0, {
                inventoryItemId: itemForContextMenu.id,
                tradeId: Number(trade.tradeId),
              });
            }}
            onClose={closeContextMenu}
          />
        )}
      </InventoryContent>

      <InventoryFooter>
        <InventoryWeight weight={inventory.weight} capacityWeight={inventory.maxWeight} />
      </InventoryFooter>
    </InventoryWrapper>
  );
}
